// The register of unfinished reports, as data. The screen renders what this
// module decides, so the ordering, the filtering, the paging and the "one value
// means no filter" rule are testable without a DOM.
//
// per docs/ui-shape.md §Parity · decision D4, docs/decizii-paritate-wireframe-2026-09-05.md
// Reference: wireframes/react/src/pages/Nefinalizate.tsx (read-only, AGENTS.md R6).
import type { DraftState } from '../../../features/reporting/api';

/** One row of the register, everything already resolved to what the cell shows. */
export interface DraftRowView {
  id: string;
  createdAtIso: string;
  siteId: string;
  siteLabel: string;
  /** The IDNP the ledger files the draft under. Never rendered raw when a name is known. */
  authorId: string;
  authorLabel: string;
  rowCount: number;
  errorCount: number;
}

export interface DraftFilters {
  siteId: string;
  authorId: string;
}

export const NO_FILTER = '';

/**
 * Resolve each draft into its row.
 *
 * `resolveSite` and `resolveAuthor` are passed in rather than looked up here
 * because both answers are i18n- and session-dependent, and this module is
 * neither. Both may fail to answer; that is the caller's fallback to choose, not
 * a reason to invent a value.
 */
export function toDraftViews(
  drafts: ReadonlyArray<DraftState>,
  resolveSite: (siteId: string) => string,
  resolveAuthor: (idnp: string) => string,
): DraftRowView[] {
  return (
    drafts
      .map((draft) => ({
        id: draft.id,
        createdAtIso: draft.createdAtIso,
        siteId: draft.siteId,
        siteLabel: resolveSite(draft.siteId),
        authorId: draft.operatorIdnp,
        // The name the draft recorded when it was started (spec 054 T065,
        // research R7); a draft from before that falls back to the resolver.
        authorLabel: draft.operatorName?.trim() || resolveAuthor(draft.operatorIdnp),
        rowCount: draft.rows.length,
        // The row's own validation verdict, as core-service persisted it on the
        // last save (draft.service.ts `upsert`). GET /stock/drafts does not
        // re-validate the way GET /stock/drafts/:id does, so a draft written
        // before a rule changed carries the older verdict until it is saved again.
        errorCount: draft.rows.filter((row) => row.validationErrors.length > 0).length,
      }))
      // Most recent first, the wireframe's stated order. It sorts on the column it
      // shows — Creat — while the endpoint orders by updatedAt, so the order is
      // asserted here rather than inherited and left to disagree with the column.
      .sort((a, b) => b.createdAtIso.localeCompare(a.createdAtIso))
  );
}

interface NamedSite {
  id: string;
  name: string;
}

/**
 * The name each location id is shown by in the register.
 *
 * A draft may sit on a location that was blocked or inactivated after the draft was
 * started (CF-28, CF-48): it is the caller's own location and a real one, so the row
 * names it, with its state beside the name, rather than reading «Locație necunoscută»
 * (BUG-1-088). The active ones come first and the inactive ones are marked; the
 * registry (an Administrator's) knows both, so it is read last and marks by its own flag.
 */
export function buildSiteNames(
  sources: {
    active: ReadonlyArray<NamedSite>;
    inactive: ReadonlyArray<NamedSite>;
    registry: ReadonlyArray<NamedSite & { active: boolean }>;
  },
  inactiveLabel: string,
): Map<string, string> {
  const byId = new Map<string, string>();
  const marked = (name: string): string => `${name} (${inactiveLabel})`;
  for (const site of sources.active) {
    if (site.id && site.name) byId.set(site.id, site.name);
  }
  for (const site of sources.inactive) {
    if (site.id && site.name) byId.set(site.id, marked(site.name));
  }
  for (const site of sources.registry) {
    if (site.id && site.name) byId.set(site.id, site.active ? site.name : marked(site.name));
  }
  return byId;
}

/**
 * What the Locație cell shows.
 *
 * A site id is a uuid: it names the location to the database and to nobody else,
 * so it is never what the cell shows — the register used to print
 * `22222222-2222-2222-2222-222222222222` at a reader (report A-26). The two ways
 * a name can be missing stay separate, because they are different sentences to
 * whoever has to act on the row: a draft that names no location at all, and an id
 * the catalog does not answer for.
 */
export function siteCellLabel(
  name: string,
  siteId: string,
  labels: { unresolved: string; unknown: string },
): string {
  return name || (siteId ? labels.unresolved : labels.unknown);
}

/** The distinct values of one facet, in first-seen order, blanks dropped. */
export function facetValues(
  rows: ReadonlyArray<DraftRowView>,
  pick: (row: DraftRowView) => string,
): string[] {
  return [...new Set(rows.map(pick))].filter((value) => value !== '');
}

/**
 * Whether the filter bar is worth showing at all.
 *
 * The wireframe hides it unless a facet has something to choose between
 * (`hasFilters` in Nefinalizate.tsx): an operator with one location and their own
 * drafts would otherwise get two controls that can only ever return the list they
 * are already looking at.
 */
export function hasUsefulFilters(rows: ReadonlyArray<DraftRowView>): boolean {
  return (
    facetValues(rows, (row) => row.siteId).length > 1 ||
    facetValues(rows, (row) => row.authorId).length > 1
  );
}

export function applyDraftFilters(
  rows: ReadonlyArray<DraftRowView>,
  filters: DraftFilters,
): DraftRowView[] {
  return rows.filter(
    (row) =>
      (filters.siteId === NO_FILTER || row.siteId === filters.siteId) &&
      (filters.authorId === NO_FILTER || row.authorId === filters.authorId),
  );
}

export const PAGE_SIZE = 10;

/** The page that actually exists, after a filter shrank the list under the cursor. */
export function clampPage(page: number, total: number, pageSize = PAGE_SIZE): number {
  return Math.min(Math.max(1, page), pageCount(total, pageSize));
}

export function pageCount(total: number, pageSize = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

export function pageSlice<T>(rows: ReadonlyArray<T>, page: number, pageSize = PAGE_SIZE): T[] {
  return rows.slice((page - 1) * pageSize, page * pageSize);
}

/** The 1-based inclusive span the footer counter names: "Rândurile 1–10 din 23". */
export function pageRange(
  page: number,
  total: number,
  pageSize = PAGE_SIZE,
): { from: number; to: number } {
  return { from: (page - 1) * pageSize + 1, to: Math.min(page * pageSize, total) };
}

/** Whether an instant falls on the calendar day the caller calls today. */
export function isSameDay(iso: string, todayIso: string): boolean {
  return iso.slice(0, 10) === todayIso;
}
